/**
 * Universal Product Master hygiene (PHASE1-AMAZON-AU-DATA-READINESS-021B).
 *
 * Canonical facts that can be entered once and reused across channels.
 * Does not model Amazon PTD, Amazon GTIN exemption, or item-vs-package splits.
 * Weight / L / W / H remain shipping (package) measurements.
 */

export const CANONICAL_CONDITIONS = ["new", "used", "refurbished"] as const;
export type CanonicalCondition = (typeof CANONICAL_CONDITIONS)[number];

export const CANONICAL_CONDITION_OPTIONS: { value: CanonicalCondition; label: string }[] = [
  { value: "new", label: "New" },
  { value: "used", label: "Used" },
  { value: "refurbished", label: "Refurbished" },
];

const PLACEHOLDER_IDENTIFIERS = new Set([
  "n/a",
  "na",
  "n.a.",
  "n.a",
  "none",
  "null",
  "nil",
  "not applicable",
  "not applicable.",
  "does not apply",
  "does not apply.",
  "not available",
  "unknown",
  "tbd",
  "tba",
  "-",
  "--",
  "—",
  ".",
]);

export type FactStatus = "present" | "missing" | "invalid/junk" | "not_applicable" | "unknown";

export interface CatalogueFactInput {
  id?: string;
  name?: string | null;
  sku?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  mpn?: string | null;
  gtin?: string | null;
  upc?: string | null;
  ean?: string | null;
  identifiersNotApplicable?: boolean;
  condition?: string | null;
  material?: string | null;
  countryOfOrigin?: string | null;
  weightKg?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  description?: string | null;
  imageCount?: number | null;
  sellingPrice?: number | null;
  costPrice?: number | null;
  hasSupplier?: boolean | null;
}

export interface CatalogueFactRow {
  field: string;
  classification:
    | "CANONICAL UNIVERSAL"
    | "CANONICAL OPTIONAL"
    | "VARIANT-SCOPED"
    | "INTERNAL PRIVATE"
    | "CHANNEL-SPECIFIC"
    | "DYNAMIC AMAZON PTD"
    | "COMPLIANCE / SAFETY";
  status: FactStatus;
  value?: string | null;
  note?: string;
}

const PLACEHOLDER_RE = /^(n\/?a|n\.a\.?|none|null|nil|not[\s-]*applicable|does[\s-]*not[\s-]*apply|not[\s-]*available|unknown|tbd|tba)$/i;

export function isPlaceholderIdentifier(raw: string | null | undefined): boolean {
  const v = (raw ?? "").trim();
  if (!v) return false;
  if (PLACEHOLDER_IDENTIFIERS.has(v.toLowerCase())) return true;
  return PLACEHOLDER_RE.test(v);
}

/** Empty / placeholder manufacturer or MPN → null. Real values pass through unchanged. */
export function sanitizeManufacturerIdentifier(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim();
  if (!v) return null;
  if (isPlaceholderIdentifier(v)) return null;
  return v;
}

export function dummyTradeIdentifierError(raw: string): string | null {
  const v = raw.trim();
  if (!v || !/^\d+$/.test(v)) return null;
  if (/^0+$/.test(v)) return "do not invent a barcode — all zeros are not a real GTIN";
  if (/^(\d)\1+$/.test(v)) return "do not invent a barcode — repeating digits are not a real GTIN";
  return null;
}

export function normalizeCanonicalCondition(raw: string | null | undefined): CanonicalCondition | null {
  const v = (raw ?? "").trim().toLowerCase().replace(/[_-]+/g, " ");
  if (!v) return null;
  if (isPlaceholderIdentifier(v)) return null;
  if (v === "new" || v === "brand new" || v === "new with tags") return "new";
  if (v === "used" || v === "pre owned" || v === "preowned") return "used";
  if (v === "refurbished" || v === "refurb" || v === "renewed") return "refurbished";
  return null;
}

/** Persistable condition: canonical lowercase, else historical non-placeholder text, else null. */
export function persistableCondition(raw: string | null | undefined): string | null {
  const canonical = normalizeCanonicalCondition(raw);
  if (canonical) return canonical;
  const v = (raw ?? "").trim();
  if (!v || isPlaceholderIdentifier(v)) return null;
  return v;
}

export function conditionSelectValue(raw: string | null | undefined): string {
  return normalizeCanonicalCondition(raw) ?? (raw ?? "").trim();
}

export type ProductDetailsPreparation = "well_prepared" | "some_details_missing";

export function evaluateProductDetailsPreparation(input: CatalogueFactInput): {
  status: ProductDetailsPreparation;
  label: string;
  missing: string[];
} {
  const missing: string[] = [];
  if (!(input.name ?? "").trim()) missing.push("name");
  if (!(input.brand ?? "").trim()) missing.push("brand");
  const ident = identifierFactStatus(input);
  if (ident === "missing" || ident === "invalid/junk") missing.push("identifier");
  if (!normalizeCanonicalCondition(input.condition) && !persistableCondition(input.condition)) missing.push("condition");
  if (sanitizeManufacturerIdentifier(input.mpn) == null && (input.mpn ?? "").trim()) {
    missing.push("mpn");
  }
  if (input.weightKg == null || Number(input.weightKg) <= 0) missing.push("package weight");
  if (input.lengthCm == null || input.widthCm == null || input.heightCm == null) missing.push("package dimensions");
  if (!(input.description ?? "").trim()) missing.push("description");
  if ((input.imageCount ?? 0) < 1) missing.push("images");
  const status: ProductDetailsPreparation = missing.length === 0 ? "well_prepared" : "some_details_missing";
  return {
    status,
    label: status === "well_prepared" ? "Product details: Well prepared" : "Product details: Some details missing",
    missing,
  };
}

export function identifierFactStatus(input: CatalogueFactInput): FactStatus {
  const gtin = (input.gtin ?? "").trim();
  const upc = (input.upc ?? "").trim();
  const ean = (input.ean ?? "").trim();
  const any = gtin || upc || ean;
  if (any) {
    if ([gtin, upc, ean].some((v) => v && (isPlaceholderIdentifier(v) || dummyTradeIdentifierError(v)))) {
      return "invalid/junk";
    }
    return "present";
  }
  if (input.identifiersNotApplicable) return "not_applicable";
  return "missing";
}

export function mpnFactStatus(raw: string | null | undefined): FactStatus {
  const v = (raw ?? "").trim();
  if (!v) return "missing";
  if (sanitizeManufacturerIdentifier(v) == null) return "invalid/junk";
  return "present";
}

export function amazonCatalogueStatusLines(details: { status: ProductDetailsPreparation }): {
  channel: string;
  productData: string;
  evaluation: string;
} {
  return {
    channel: "Amazon AU",
    productData: details.status === "well_prepared" ? "Product data prepared" : "Product data incomplete",
    evaluation: "Live Amazon requirements not evaluated",
  };
}

export function formatAmazonCatalogueStatus(details: { status: ProductDetailsPreparation }): string {
  const lines = amazonCatalogueStatusLines(details);
  return `${lines.channel} — ${lines.productData}. ${lines.evaluation}`;
}

export function amazonStatusNeverReady(text: string): boolean {
  return !/amazon\s+ready/i.test(text);
}

export const GOVERNED_MATERIAL_ATTRIBUTE = "Material";

export const MEASUREMENT_SEMANTICS =
  "weight_kg / length_cm / width_cm / height_cm are package (shipping) measurements. Item-only measurements are not modelled.";

export function auditCatalogueFacts(input: CatalogueFactInput): CatalogueFactRow[] {
  const ident = identifierFactStatus(input);
  const mpn = mpnFactStatus(input.mpn);
  const cond = persistableCondition(input.condition);
  const manufacturer = sanitizeManufacturerIdentifier(input.manufacturer);
  const origin = sanitizeManufacturerIdentifier(input.countryOfOrigin);
  const material = (input.material ?? "").trim();
  return [
    { field: "name", classification: "CANONICAL UNIVERSAL", status: (input.name ?? "").trim() ? "present" : "missing", value: input.name ?? null },
    { field: "sku", classification: "VARIANT-SCOPED", status: input.sku == null ? "unknown" : (input.sku.trim() ? "present" : "missing"), value: input.sku ?? null },
    { field: "brand", classification: "CANONICAL UNIVERSAL", status: (input.brand ?? "").trim() ? "present" : (input.brand == null ? "unknown" : "missing"), value: input.brand ?? null },
    {
      field: "manufacturer",
      classification: "CANONICAL OPTIONAL",
      status: input.manufacturer == null ? "unknown" : manufacturer ? "present" : (input.manufacturer.trim() ? "invalid/junk" : "missing"),
      value: input.manufacturer ?? null,
    },
    {
      field: "mpn",
      classification: "CANONICAL OPTIONAL",
      status: input.mpn == null ? "unknown" : mpn,
      value: input.mpn ?? null,
      note: mpn === "invalid/junk" ? "Placeholder MPN is not a real manufacturer part number. Store as absent." : undefined,
    },
    {
      field: "gtin/upc/ean",
      classification: "CANONICAL OPTIONAL",
      status: ident,
      note: ident === "not_applicable"
        ? "Canonical identifier absent / not applicable. This is not Amazon GTIN exemption."
        : ident === "invalid/junk"
          ? "Do not invent barcodes."
          : undefined,
    },
    {
      field: "identifiers_not_applicable",
      classification: "CANONICAL OPTIONAL",
      status: input.identifiersNotApplicable ? "present" : "missing",
      note: "Never interpret as Amazon GTIN exemption. Amazon exemption is listing-specific and unevaluated.",
    },
    {
      field: "condition",
      classification: "CANONICAL UNIVERSAL",
      status: input.condition == null ? "unknown" : cond ? "present" : (input.condition.trim() ? "invalid/junk" : "missing"),
      value: input.condition ?? null,
    },
    {
      field: "material",
      classification: "CANONICAL OPTIONAL",
      status: input.material == null ? "unknown" : material ? "present" : "missing",
      value: input.material ?? null,
      note: "Governed product_attributes name Material — not a dedicated column.",
    },
    {
      field: "country_of_origin",
      classification: "CANONICAL OPTIONAL",
      status: input.countryOfOrigin == null ? "unknown" : origin ? "present" : (input.countryOfOrigin.trim() ? "invalid/junk" : "missing"),
      value: input.countryOfOrigin ?? null,
    },
    {
      field: "package_weight",
      classification: "CANONICAL OPTIONAL",
      status: input.weightKg == null ? "unknown" : Number(input.weightKg) > 0 ? "present" : "missing",
      note: MEASUREMENT_SEMANTICS,
    },
    {
      field: "package_dimensions",
      classification: "CANONICAL OPTIONAL",
      status:
        input.lengthCm == null && input.widthCm == null && input.heightCm == null
          ? "unknown"
          : input.lengthCm != null && input.widthCm != null && input.heightCm != null
            ? "present"
            : "missing",
      note: MEASUREMENT_SEMANTICS,
    },
    {
      field: "description",
      classification: "CANONICAL UNIVERSAL",
      status: input.description == null ? "unknown" : input.description.trim() ? "present" : "missing",
    },
    {
      field: "images",
      classification: "CANONICAL UNIVERSAL",
      status: input.imageCount == null ? "unknown" : input.imageCount > 0 ? "present" : "missing",
    },
    {
      field: "selling_price",
      classification: "CANONICAL UNIVERSAL",
      status: input.sellingPrice == null ? "unknown" : Number(input.sellingPrice) > 0 ? "present" : "missing",
    },
    {
      field: "cost",
      classification: "INTERNAL PRIVATE",
      status: input.costPrice == null ? "unknown" : "present",
      note: "Never marketplace-eligible by presence.",
    },
    {
      field: "supplier",
      classification: "INTERNAL PRIVATE",
      status: input.hasSupplier == null ? "unknown" : input.hasSupplier ? "present" : "missing",
      note: "Internal. Does not imply marketplace eligibility.",
    },
    {
      field: "amazon_product_type",
      classification: "DYNAMIC AMAZON PTD",
      status: "not_applicable",
      note: "Must not enter Product Master.",
    },
    {
      field: "amazon_gtin_exemption",
      classification: "CHANNEL-SPECIFIC",
      status: "not_applicable",
      note: "Listing-specific future authority. identifiers_not_applicable is not exemption.",
    },
  ];
}

/** Deterministic fixtures — only facts the ticket/certification already established. Other cells stay unknown. */
export const PVC_BRIDLE_ID = "9c5d16f7-f444-4b7d-bb34-97d5bfd8d729";
export const DOG_COLLAR_ID = "0411c996-6509-4c90-b21f-086d326cdb4c";

export const PVC_BRIDLE_OBSERVED: CatalogueFactInput = {
  id: PVC_BRIDLE_ID,
  name: "PVC Bridle with Padding and Rubber Reins",
  mpn: "N/A",
};

export const DOG_COLLAR_OBSERVED: CatalogueFactInput = {
  id: DOG_COLLAR_ID,
  name: "Premium Padded Dog Collar",
};

export function parseCanonicalImportFields(get: (header: string) => string): {
  brand: string | null;
  manufacturer: string | null;
  mpn: string | null;
  condition: string | null;
  country_of_origin: string | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  gtin: string | null;
  upc: string | null;
  ean: string | null;
  identifiers_not_applicable: boolean;
  errors: string[];
} {
  const num = (primary: string, alias: string): { value: number | null; raw: string } => {
    const raw = get(alias) || get(primary);
    if (!raw) return { value: null, raw: "" };
    const n = Number(raw);
    return { value: Number.isFinite(n) ? n : NaN, raw };
  };
  const errors: string[] = [];
  const weight = num("weight_kg", "package_weight_kg");
  const length = num("length_cm", "package_length_cm");
  const width = num("width_cm", "package_width_cm");
  const height = num("height_cm", "package_height_cm");
  const measureErr = (field: string, raw: string, n: number | null) => {
    if (!raw) return;
    if (n == null || Number.isNaN(n)) errors.push(`${field}: must be a number`);
    else if (n <= 0) errors.push(`${field}: package measurement must be greater than zero`);
  };
  measureErr("weight_kg", weight.raw, weight.value);
  measureErr("length_cm", length.raw, length.value);
  measureErr("width_cm", width.raw, width.value);
  measureErr("height_cm", height.raw, height.value);

  const rawCondition = get("condition");
  if (rawCondition && !isPlaceholderIdentifier(rawCondition) && !normalizeCanonicalCondition(rawCondition)) {
    errors.push("condition: must be new, used, or refurbished — leave blank if unknown");
  }

  const identifiers_not_applicable = /^(true|1|yes)$/i.test(get("identifiers_not_applicable"));
  const gtin = identifiers_not_applicable ? null : get("gtin") || null;
  const upc = identifiers_not_applicable ? null : get("upc") || null;
  const ean = identifiers_not_applicable ? null : get("ean") || null;
  if (identifiers_not_applicable && (get("gtin") || get("upc") || get("ean"))) {
    errors.push("gtin: cannot combine a barcode with identifiers_not_applicable (not Amazon exemption)");
  }
  return {
    brand: get("brand") || null,
    manufacturer: sanitizeManufacturerIdentifier(get("manufacturer")),
    mpn: sanitizeManufacturerIdentifier(get("mpn")),
    condition: persistableCondition(rawCondition),
    country_of_origin: sanitizeManufacturerIdentifier(get("country_of_origin")),
    weight_kg: weight.value != null && !Number.isNaN(weight.value) && weight.value > 0 ? weight.value : null,
    length_cm: length.value != null && !Number.isNaN(length.value) && length.value > 0 ? length.value : null,
    width_cm: width.value != null && !Number.isNaN(width.value) && width.value > 0 ? width.value : null,
    height_cm: height.value != null && !Number.isNaN(height.value) && height.value > 0 ? height.value : null,
    gtin,
    upc,
    ean,
    identifiers_not_applicable,
    errors,
  };
}
